import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Inbox, MessageSquare, Clapperboard, UploadCloud, Users, Wallet, PiggyBank, BarChart3, Settings as SettingsIcon, LogOut, ExternalLink, Film, Activity as ActivityIcon, FlaskConical, Lightbulb, LibraryBig, CalendarDays, BookOpen } from "lucide-react";
import { MeltoLogo } from "@/components/MeltoLogo";
import { NotificationBell } from "@/components/dashboard/NotificationBell";
import { AdminActivity } from "@/components/admin/AdminActivity";
import { AdminTestMode } from "@/components/admin/AdminTestMode";
import { AdminIdeas } from "@/components/admin/AdminIdeas";
import { AdminLibrary } from "@/components/admin/AdminLibrary";
import { AdminManuals } from "@/components/manuals/AdminManuals";
import { AdminCalendar } from "@/components/admin/AdminCalendar";
import { CommandPalette } from "@/components/admin/CommandPalette";
import { AdminTestPack } from "@/components/admin/AdminTestPack";
import { AdminOverview } from "@/components/admin/AdminOverview";
import { AdminApplications } from "@/components/admin/AdminApplications";
import { AdminMessages } from "@/components/admin/AdminMessages";
import { AdminProduction } from "@/components/admin/AdminProduction";
import { AdminSubmissions } from "@/components/admin/AdminSubmissions";
import { AdminVideos } from "@/components/admin/AdminVideos";
import { AdminTeam } from "@/components/admin/AdminTeam";
import { AdminPayments } from "@/components/admin/AdminPayments";
import { AdminBudget } from "@/components/admin/AdminBudget";
import { AdminAnalytics } from "@/components/admin/AdminAnalytics";
import { AdminSettings } from "@/components/admin/AdminSettings";
import { ThemeToggle } from "@/components/dashboard/ThemeToggle";
import { API, authHeaders } from "@/constants/roles";
import { initDisplayCurrency, useCurrency } from "@/utils/currency";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "applications", label: "Applications", icon: Inbox },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "ideas", label: "Ideas", icon: Lightbulb },
  { id: "library", label: "Library", icon: LibraryBig },
  { id: "manuals", label: "Manuals", icon: BookOpen },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "testpack", label: "Test Pack", icon: FlaskConical },
  { id: "production", label: "Production", icon: Clapperboard },
  { id: "submissions", label: "Submissions", icon: UploadCloud },
  { id: "videos", label: "Videos", icon: Film },
  { id: "team", label: "Team", icon: Users },
  { id: "payments", label: "Payments", icon: Wallet },
  { id: "budget", label: "Budget", icon: PiggyBank },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "activity", label: "Activity", icon: ActivityIcon },
  { id: "testmode", label: "Test Mode", icon: FlaskConical },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

export const AdminApp = ({ me, theme, onToggleTheme }) => {
  const [section, setSection] = useState("overview");
  const [msgUnread, setMsgUnread] = useState(0);
  const [videoToOpen, setVideoToOpen] = useState(null);
  const navigate = useNavigate();
  // Owner display currency: subscribing here re-renders the whole console on toggle,
  // so every fmtMoney call re-evaluates with the fresh rate.
  const { currency: displayCurrency } = useCurrency();
  useEffect(() => { initDisplayCurrency(); }, []);

  const openVideo = useCallback((videoId) => {
    setVideoToOpen(videoId);
    setSection("videos");
  }, []);

  // Calendar/deep-link navigation: open the right section, then scroll to + flash the source record
  const openRecord = useCallback((targetSection, testid) => {
    setSection(targetSection);
    if (!testid) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-cyan-400");
        setTimeout(() => el.classList.remove("ring-2", "ring-cyan-400"), 2600);
      }
    }, 450);
  }, []);

  const refreshUnread = useCallback(() => {
    axios
      .get(`${API}/admin/messages/unread`, { headers: authHeaders() })
      .then((r) => setMsgUnread(r.data.unread))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 20000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  const logout = () => {
    localStorage.removeItem("melto_token");
    navigate("/login");
  };

  const sections = {
    overview: <AdminOverview onNavigate={setSection} />,
    applications: <AdminApplications />,
    messages: <AdminMessages onRead={refreshUnread} canDelete={me?.role === "admin" && !me?.demo} />,
    ideas: <AdminIdeas canDelete />,
    library: <AdminLibrary canDelete />,
    manuals: <AdminManuals />,
    calendar: <AdminCalendar onNavigate={openRecord} />,
    testpack: <AdminTestPack />,
    production: <AdminProduction />,
    submissions: <AdminSubmissions />,
    videos: <AdminVideos openVideoId={videoToOpen} onConsumeOpen={() => setVideoToOpen(null)} />,
    team: <AdminTeam onOpenVideo={openVideo} />,
    payments: <AdminPayments />,
    budget: <AdminBudget />,
    analytics: <AdminAnalytics />,
    activity: <AdminActivity />,
    testmode: <AdminTestMode />,
    settings: <AdminSettings me={me} />,
  };

  const badge = (id) =>
    id === "messages" && msgUnread > 0 ? (
      <span data-testid="admin-messages-badge" className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
        {msgUnread}
      </span>
    ) : null;

  const navButton = ({ id, label, icon: Icon }, mobile = false) => (
    <button
      key={id}
      data-testid={`admin-nav-${id}`}
      onClick={() => setSection(id)}
      className={
        mobile
          ? `flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              section === id ? "bg-cyan-500 text-white" : "bg-[var(--pv-subtle)] text-[var(--pv-text-3)] border border-[var(--pv-border)]"
            }`
          : `flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
              section === id ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)] hover:bg-[var(--pv-subtle)] hover:text-[var(--pv-text)]"
            }`
      }
    >
      <Icon className="h-4 w-4" />
      {label}
      {badge(id)}
    </button>
  );

  return (
    <div data-testid="admin-app" className="flex min-h-screen bg-[var(--pv-bg)] text-[var(--pv-text-2)]">
      <CommandPalette onNavigate={setSection} />
      <aside className="relative z-30 hidden w-60 shrink-0 flex-col border-r border-[var(--pv-border)] bg-[var(--pv-bg)] md:flex">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <MeltoLogo size={36} testid="admin-logo-mark" />
          <div>
            <p className="font-display text-sm font-bold text-[var(--pv-text)]">Melto Studios</p>
            <p className="font-mono-label text-[9px] font-bold uppercase tracking-widest text-cyan-400">Admin console</p>
          </div>
          <div className="ml-auto"><NotificationBell align="left" /></div>
        </div>
        <nav data-testid="admin-nav" className="flex-1 space-y-1 px-3 py-2">
          {NAV.filter((item) => !(me?.demo && item.id === "testmode")).map((item) => navButton(item))}
        </nav>
        <div className="space-y-1 border-t border-[var(--pv-border)] px-3 py-4">
          <div className="flex items-center gap-2 px-2 pb-1">
            <ThemeToggle theme={theme} onToggle={onToggleTheme} />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{theme === "dark" ? "Dark" : "Light"} mode</span>
            {displayCurrency === "AUD" && (
              <span data-testid="currency-viewing-badge" className="ml-auto rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-cyan-300">Viewing AUD</span>
            )}
          </div>
          <Link to="/" data-testid="admin-view-site-link" className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-[var(--pv-text-3)] transition-colors hover:bg-[var(--pv-subtle)] hover:text-[var(--pv-text)]">
            <ExternalLink className="h-4 w-4" /> View site
          </Link>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-[var(--pv-text-3)] transition-colors hover:bg-[var(--pv-subtle)] hover:text-[var(--pv-text)]">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 border-b border-[var(--pv-border)] bg-[var(--pv-header)] backdrop-blur-xl md:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <MeltoLogo size={32} testid="admin-mobile-logo-mark" />
              <span className="font-display text-sm font-bold text-[var(--pv-text)]">Admin</span>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <ThemeToggle theme={theme} onToggle={onToggleTheme} />
            </div>
          </div>
          <nav className="flex gap-2 overflow-x-auto px-4 pb-3">
            {NAV.filter((item) => !(me?.demo && item.id === "testmode")).map((item) => navButton(item, true))}
            <button data-testid="admin-logout-btn-mobile" onClick={logout} className="flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--pv-raised)] px-4 py-2 text-xs font-semibold text-[var(--pv-text)]">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </nav>
        </header>
        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">{sections[section]}</main>
      </div>
    </div>
  );
};
